import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MenuItem, MessageService} from 'primeng/api';
import {GatewayPublicUserService, UserDTO} from '@tj/tj-dmp-lib-api';
import {KeycloakService} from 'keycloak-angular';
import {switchMap} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {TranslateService} from '@ngx-translate/core';
import {LANGUAGES, LangService} from '../../services/lang.service';

@Component({
    templateUrl: './account.component.html',
    styleUrls: ['./account.component.scss'],
    standalone: false
})
export class AccountComponent implements OnInit {

	breadcrumb: MenuItem[] = [
		{ label: this.translate.instant('menu.users.account') }
	];

	readonly languages = LANGUAGES;

	actual: UserDTO;

	form: UntypedFormGroup;

	saving = false;

	sendingReset = false;

	resetEmailSent = false;

	get displayName(): string {
		const fullName = [this.actual.firstName, this.actual.lastName].filter(Boolean).join(' ');
		return fullName || this.actual.login;
	}

	constructor(private fb: UntypedFormBuilder,
		private gatewayPublicUserService: GatewayPublicUserService,
		private messageService: MessageService,
		protected readonly keycloakService: KeycloakService,
		private readonly translate: TranslateService,
		private langService: LangService
	) { }

	ngOnInit() {
		this.form = this.fb.group({
			firstname: ['', Validators.required],
			lastname: ['', Validators.required],
			lang: [null, Validators.required],
		});
		this.gatewayPublicUserService.getCurrentUser().subscribe((user) => {
			this.actual = user;
			this.form.reset(this.savedValues());
		});
	}

	// Values as saved on the user: the form starts from them, and "Annuler" goes back to them.
	private savedValues() {
		return {
			firstname: this.actual.firstName,
			lastname: this.actual.lastName,
			lang: this.languages.find(lang => lang.code === this.actual.langKey?.toLowerCase())?.code ?? null,
		};
	}

	// A field's error is shown under it once the user has changed or left it, not on a pristine form.
	showError(field: string): boolean {
		const control = this.form.get(field);
		return control.invalid && (control.dirty || control.touched);
	}

	cancel() {
		this.form.reset(this.savedValues());
	}

	logout() {
		this.keycloakService.init({
			config: {
				url: environment.KeycloakUrl,
				realm: environment.KeycloakRealm,
				clientId: environment.KeycloakClientId
			},
			initOptions: {
				pkceMethod: 'S256',
				checkLoginIframe: false,
				redirectUri: environment.URL_DMP,
			}
		}).then(() => {
			this.keycloakService.logout();
		});
	}

	save() {
		if (this.form.invalid || this.form.pristine || this.saving) {
			this.form.markAllAsTouched();
			return;
		}
		this.saving = true;
		const {firstname, lastname, lang} = this.form.value;

		// Fetch the full user object and update it (PUT /api/users)
		this.gatewayPublicUserService.getCurrentUser().pipe(
			switchMap(user => this.gatewayPublicUserService.updateCurrentUser({...user, firstName: firstname, lastName: lastname, langKey: lang}))
		).subscribe({
			next: () => {
				this.langService.applyUserLanguage(lang);
				this.messageService.add({
					key: 'common', severity: 'success', summary: this.translate.instant('account.toasts.saveSuccessSummary'),
					detail: this.translate.instant('account.toasts.saveSuccessDetail')
				});
				// "saving" stays true: the form is left as is until the logout.
				setTimeout(() => {
					this.logout();
				}, 5000);
			},
			error: (e) => {
				console.error('Error updating user:', e);
				this.saving = false;
				this.messageService.add({
					key: 'common', severity: 'error', summary: this.translate.instant('account.toasts.errorSummary'), detail: this.translate.instant('account.toasts.saveErrorDetail')
				});
			}
		});
	}

	sendPasswordResetEmail() {
		this.sendingReset = true;
		this.resetEmailSent = false;
		this.gatewayPublicUserService.executeAction(this.actual.id).subscribe({
			next: () => {
				this.sendingReset = false;
				this.resetEmailSent = true;
				this.messageService.add({
					key: 'common', severity: 'success', summary: this.translate.instant('account.toasts.resetEmailSentSummary'),
					detail: this.translate.instant('account.toasts.resetEmailSentDetail', {email: this.actual.email})
				});
			},
			error: (e) => {
				console.error('Error sending password reset email:', e);
				this.sendingReset = false;
				this.messageService.add({
					key: 'common', severity: 'error', summary: this.translate.instant('account.toasts.errorSummary'), detail: this.translate.instant('account.toasts.resetEmailErrorDetail')
				});
			}
		});
	}
}
